import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUrlParams } from '../apiService/timespam';
import { API_BASE_URL } from '../apiService/apiClient';
import './WomenOnboarding.css';

const BASE_URL = `${API_BASE_URL}/goqiiapihealthactivity`;
const MIN_SELECTABLE_DAYS_AGO = 60;
const DOB_MIN_AGE = 10;
const DOB_MAX_AGE = 65;
const WHEEL_ITEM_HEIGHT = 48;
const WHEEL_VISIBLE_ROWS = 5;
const CYCLE_LENGTH_OPTIONS = Array.from({ length: 50 - 15 + 1 }, (_, i) => 15 + i);

const STEPS = ['type', 'proceed', 'cycleLength', 'periodStart', 'periodLength', 'dob'];

const NumberWheelPicker = ({ options, value, onChange, ariaLabel }) => {
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const velocityRef = useRef(0);
  const lastMoveRef = useRef(null);
  const momentumFrameRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const index = options.indexOf(value);
      scrollRef.current.scrollTop = index * WHEEL_ITEM_HEIGHT;
    }
    return () => {
      if (momentumFrameRef.current) cancelAnimationFrame(momentumFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live-update the highlighted value as the wheel scrolls. Snapping itself is left to
  // the browser's native CSS scroll-snap (touch/wheel) so momentum stays iOS-smooth —
  // forcing our own scrollTo here would fight that native deceleration and cause jank.
  // Mouse-drag has no native momentum, so it gets explicit fling physics below instead.
  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const rawIndex = Math.round(scrollTop / WHEEL_ITEM_HEIGHT);
    const index = Math.max(0, Math.min(options.length - 1, rawIndex));
    onChange(options[index]);
  };

  const snapToNearest = (smooth = true) => {
    if (!scrollRef.current) return;
    const maxScroll = scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
    const clampedTop = Math.max(0, Math.min(maxScroll, scrollRef.current.scrollTop));
    const index = Math.max(0, Math.min(options.length - 1, Math.round(clampedTop / WHEEL_ITEM_HEIGHT)));
    scrollRef.current.scrollTo({ top: index * WHEEL_ITEM_HEIGHT, behavior: smooth ? 'smooth' : 'auto' });
  };

  // iOS-style deceleration: keep coasting at a shrinking velocity each frame,
  // then settle on the nearest item once it's slow enough or hits an edge.
  const runMomentum = () => {
    if (!scrollRef.current) return;
    const FRICTION = 0.94;
    velocityRef.current *= FRICTION;

    const maxScroll = scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
    const next = Math.max(0, Math.min(maxScroll, scrollRef.current.scrollTop + velocityRef.current * 16));
    scrollRef.current.scrollTop = next;

    if (Math.abs(velocityRef.current) > 0.02 && next > 0 && next < maxScroll) {
      momentumFrameRef.current = requestAnimationFrame(runMomentum);
    } else {
      momentumFrameRef.current = null;
      snapToNearest();
    }
  };

  const handlePointerDown = (e) => {
    if (e.pointerType !== 'mouse' || !scrollRef.current) return;
    if (momentumFrameRef.current) {
      cancelAnimationFrame(momentumFrameRef.current);
      momentumFrameRef.current = null;
    }
    dragRef.current = { startY: e.clientY, startScrollTop: scrollRef.current.scrollTop };
    lastMoveRef.current = { y: e.clientY, time: performance.now() };
    velocityRef.current = 0;
    scrollRef.current.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current || e.pointerType !== 'mouse' || !scrollRef.current) return;
    scrollRef.current.scrollTop = dragRef.current.startScrollTop - (e.clientY - dragRef.current.startY);

    const now = performance.now();
    const dt = now - lastMoveRef.current.time;
    if (dt > 0) {
      velocityRef.current = -(e.clientY - lastMoveRef.current.y) / dt;
    }
    lastMoveRef.current = { y: e.clientY, time: now };
  };

  const endDrag = (e) => {
    if (!dragRef.current || e.pointerType !== 'mouse') return;
    dragRef.current = null;
    if (Math.abs(velocityRef.current) > 0.05) {
      momentumFrameRef.current = requestAnimationFrame(runMomentum);
    } else {
      snapToNearest();
    }
  };

  return (
    <div className="onb-wheel-card">
      <div className="onb-wheel-highlight" />
      <div
        className="onb-wheel-scroll"
        ref={scrollRef}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        role="slider"
        aria-label={ariaLabel}
        aria-valuenow={value}
        style={{ height: WHEEL_ITEM_HEIGHT * WHEEL_VISIBLE_ROWS }}
      >
        <div className="onb-wheel-spacer" style={{ height: WHEEL_ITEM_HEIGHT * Math.floor(WHEEL_VISIBLE_ROWS / 2) }} />
        {options.map((val) => (
          <div
            key={val}
            className="onb-wheel-item"
            data-distance={Math.min(4, Math.abs(val - value))}
            style={{ height: WHEEL_ITEM_HEIGHT }}
          >
            {val}
          </div>
        ))}
        <div className="onb-wheel-spacer" style={{ height: WHEEL_ITEM_HEIGHT * Math.floor(WHEEL_VISIBLE_ROWS / 2) }} />
      </div>
    </div>
  );
};

const WomenOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userid } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const queryUserId = queryParams.get('goqiiUserId');
  const genderParam = (queryParams.get('gender') || '').toLowerCase();

  const { lang, encryptedId } = getUrlParams();
  const USER_ID = location.state?.goqiiUserId || encryptedId || userid || queryUserId;

  const hasTypeStep = genderParam === 'male' || genderParam === 'female';
  const [stepIndex, setStepIndex] = useState(hasTypeStep ? 0 : 2);
  const [trackingType, setTrackingType] = useState('');
  const [cycleLength, setCycleLength] = useState(28);
  const [periodLength, setPeriodLength] = useState(5);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [dobDay, setDobDay] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const step = STEPS[stepIndex];

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const monthNames = t('cycleTracker.months', { returnObjects: true });
  const dayNames = t('cycleTracker.days', { returnObjects: true });

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const previousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minSelectableDate = new Date(today);
  minSelectableDate.setDate(minSelectableDate.getDate() - MIN_SELECTABLE_DAYS_AGO);

  const isDateDisabled = (date) => date > today || date < minSelectableDate;

  const handleDateClick = (day) => {
    const clicked = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (isDateDisabled(clicked)) return;
    setPeriodStartDate(clicked);
  };

  const getDayClass = (day) => {
    const classes = ['onb-calendar-day'];
    const full = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (isDateDisabled(full)) classes.push('disabled');
    if (periodStartDate && full.getTime() === periodStartDate.getTime()) classes.push('range-start');
    return classes.join(' ');
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="onb-calendar-day empty" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <div key={day} className={getDayClass(day)} onClick={() => handleDateClick(day)}>
          {day}
        </div>
      );
    }
    return days;
  };

  const selectType = (type) => {
    setTrackingType(type);
    setStepIndex(STEPS.indexOf('proceed'));
  };

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));

  const currentYear = new Date().getFullYear();
  const dobYearOptions = [];
  for (let y = currentYear - DOB_MIN_AGE; y >= currentYear - DOB_MAX_AGE; y--) {
    dobYearOptions.push(y);
  }
  const dobDayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  const submitOnboarding = async () => {
    if (!dobDay || !dobMonth || !dobYear) {
      setError(t('womenOnboarding.selectDobError', 'Please select your date of birth'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const dob = `${dobYear}-${String(dobMonth).padStart(2, '0')}-${String(dobDay).padStart(2, '0')}`;
      await fetch(`${BASE_URL}/save_women_log_by_dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', lang },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          cycleLength,
          startDate: formatDate(periodStartDate),
          periodLength,
          dob,
          popuptype: trackingType
        })
      });
      navigate(`/goal-new${location.search}`, { replace: true, state: { goqiiUserId: USER_ID } });
    } catch (err) {
      console.error(err);
      setError(t('womenOnboarding.saveError', 'Failed to save. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = genderParam === 'male'
    ? [
        { value: 'partner', label: t('womenOnboarding.typePartner', 'Partner'), primary: true },
        { value: 'relative', label: t('womenOnboarding.typeRelative', 'Relative') },
        { value: 'friend', label: t('womenOnboarding.typeFriend', 'Friend') },
        { value: 'skip', label: t('womenOnboarding.typeSkip', 'Skip') }
      ]
    : [
        { value: 'self', label: t('womenOnboarding.typeSelf', 'Self'), primary: true },
        { value: 'relative', label: t('womenOnboarding.typeRelative', 'Relative') },
        { value: 'friend', label: t('womenOnboarding.typeFriend', 'Friend') },
        { value: 'skip', label: t('womenOnboarding.typeSkip', 'Skip') }
      ];

  return (
    <div className="women-onboarding">
      {error && (
        <div className="onb-error-banner">
          <span>{error}</span>
          <button className="onb-error-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {step === 'type' && (
        <div className="onb-modal-overlay">
          <div className="onb-modal-content">
            <p className="onb-modal-text">{t('womenOnboarding.typeQuestion', 'Who do you want to track menstrual cycle/pregnancy for?')}</p>
            <div className="onb-type-grid">
              {typeOptions.map((opt) => (
                <button
                  key={opt.value}
                  className={`onb-type-btn${opt.primary ? ' primary' : ''}`}
                  onClick={() => selectType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 'proceed' && (
        <div className="onb-modal-overlay">
          <div className="onb-modal-content">
            <p className="onb-modal-text">{t('womenOnboarding.proceedText', 'To proceed, please answer a few important questions.')}</p>
            <button className="onb-modal-ok-btn" onClick={goNext}>
              {t('womenOnboarding.ok', 'Ok')}
            </button>
          </div>
        </div>
      )}

      {step === 'cycleLength' && (
        <div className="onb-step">
          <img src="https://appcdn.goqii.com/storeimg/99165_1627472246.png" alt="" className="onb-cycle-img" />
          <h4 className="onb-question">{t('womenOnboarding.avgCycleQuestion', 'How long is your average cycle?')}</h4>

          <NumberWheelPicker
            options={CYCLE_LENGTH_OPTIONS}
            value={cycleLength}
            onChange={setCycleLength}
            ariaLabel={t('womenOnboarding.avgCycleQuestion', 'How long is your average cycle?')}
          />

          <div className="onb-actions">
            <button className="onb-next-btn active" onClick={goNext}>
              {t('womenOnboarding.next', 'Next')}
            </button>
          </div>
        </div>
      )}

      {step === 'periodStart' && (
        <div className="onb-step">
          <h4 className="onb-question">{t('womenOnboarding.lastPeriodQuestion', 'When did your last period start?')}</h4>

          <div className="onb-calendar-container">
            <div className="onb-calendar-header">
              <button className="onb-nav-btn" onClick={previousMonth} aria-label="Previous month">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <h2 className="onb-month-year">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
              <button className="onb-nav-btn" onClick={nextMonth} aria-label="Next month">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
            <div className="onb-calendar-grid">
              {dayNames.map((d) => <div key={d} className="onb-day-name">{d}</div>)}
              {renderCalendar()}
            </div>
          </div>

          <div className="onb-actions">
            <button
              className={`onb-next-btn${periodStartDate ? ' active' : ' disabled'}`}
              onClick={goNext}
              disabled={!periodStartDate}
            >
              {t('womenOnboarding.next', 'Next')}
            </button>
          </div>
        </div>
      )}

      {step === 'periodLength' && (
        <div className="onb-step">
          <h4 className="onb-question">{t('womenOnboarding.periodLengthQuestion', 'How long did it last?')}</h4>

          <div className="onb-cycle-picker">
            <div className="onb-cycle-value">{periodLength}</div>
            <input
              type="range"
              min="1"
              max="10"
              value={periodLength}
              onChange={(e) => setPeriodLength(Number(e.target.value))}
              className="onb-cycle-slider"
              aria-label={t('womenOnboarding.periodLengthQuestion', 'How long did it last?')}
            />
            <div className="onb-cycle-range-labels">
              <span>1</span>
              <span>10</span>
            </div>
          </div>

          <div className="onb-actions">
            <button className="onb-next-btn active" onClick={goNext}>
              {t('womenOnboarding.next', 'Next')}
            </button>
          </div>
        </div>
      )}

      {step === 'dob' && (
        <div className="onb-step">
          <h4 className="onb-question">{t('womenOnboarding.dobQuestion', 'When is your birthday?')}</h4>

          <div className="onb-dob-picker">
            <select className="onb-dob-select" value={dobDay} onChange={(e) => setDobDay(e.target.value)} aria-label={t('womenOnboarding.day', 'Day')}>
              <option value="">{t('womenOnboarding.day', 'Day')}</option>
              {dobDayOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="onb-dob-select" value={dobMonth} onChange={(e) => setDobMonth(e.target.value)} aria-label={t('womenOnboarding.month', 'Month')}>
              <option value="">{t('womenOnboarding.month', 'Month')}</option>
              {monthNames.map((m, idx) => <option key={m} value={idx + 1}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
            </select>
            <select className="onb-dob-select" value={dobYear} onChange={(e) => setDobYear(e.target.value)} aria-label={t('womenOnboarding.year', 'Year')}>
              <option value="">{t('womenOnboarding.year', 'Year')}</option>
              {dobYearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="onb-actions">
            <button
              className={`onb-next-btn${(dobDay && dobMonth && dobYear) ? ' active' : ' disabled'}`}
              onClick={submitOnboarding}
              disabled={!dobDay || !dobMonth || !dobYear || loading}
            >
              {loading ? t('womenOnboarding.saving', 'Saving...') : t('womenOnboarding.next', 'Next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WomenOnboarding;
